import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import axios from 'axios';

interface M2MPoint {
  ts: string;
  mtm: number;
  pnl: number;
  exposure: number;
}

interface M2MData {
  strategy_id: string;
  interval: string;
  points: M2MPoint[];
}

interface M2MChartProps {
  strategyId: string;
  interval?: string;
  height?: number;
}

const M2MChart: React.FC<M2MChartProps> = ({
  strategyId,
  interval = '1m',
  height = 300
}) => {
  const [m2mData, setM2mData] = useState<M2MData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchM2MData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // Last 24 hours

      const response = await axios.get<M2MData>(
        `/api/v1/algo/strategies/${strategyId}/mtm`,
        {
          params: {
            start: startTime,
            end: endTime,
            interval
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}` // Assuming token stored in localStorage
          }
        }
      );

      setM2mData(response.data);
    } catch (err) {
      console.error('Failed to fetch M2M data:', err);
      setError('Failed to load M2M data');
      
      // Generate fallback mock data for demonstration
      const mockData: M2MData = {
        strategy_id: strategyId,
        interval,
        points: generateMockM2MData()
      };
      setM2mData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const generateMockM2MData = (): M2MPoint[] => {
    const points: M2MPoint[] = [];
    const now = Date.now();
    
    for (let i = 0; i < 100; i++) {
      const timestamp = new Date(now - (99 - i) * 60 * 1000); // 1-minute intervals
      const baseValue = 10000;
      const randomWalk = (Math.random() - 0.5) * 200;
      const currentValue = baseValue + randomWalk;
      
      points.push({
        ts: timestamp.toISOString(),
        mtm: currentValue,
        pnl: currentValue - baseValue,
        exposure: Math.abs(currentValue) * 1.2
      });
    }
    
    return points;
  };

  useEffect(() => {
    fetchM2MData();
    
    // Set up auto-refresh every 30 seconds
    const interval_id = setInterval(fetchM2MData, 30000);
    
    return () => clearInterval(interval_id);
  }, [strategyId, interval]);

  if (loading) {
    return (
      <div className="m2m-chart-loading" style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1f1f1f',
        color: '#8c8c8c'
      }}>
        <div>Loading M2M data...</div>
      </div>
    );
  }

  if (error && !m2mData) {
    return (
      <div className="m2m-chart-error" style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1f1f1f',
        color: '#ff4d4f'
      }}>
        <div>Error: {error}</div>
      </div>
    );
  }

  const chartData = m2mData?.points.map(point => ([
    new Date(point.ts).getTime(),
    point.mtm
  ])) || [];

  const pnlData = m2mData?.points.map(point => ([
    new Date(point.ts).getTime(),
    point.pnl
  ])) || [];

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'line',
      backgroundColor: '#1f1f1f',
      height,
      spacing: [10, 10, 15, 10]
    },
    title: {
      text: `Mark-to-Market (M2M) - ${strategyId}`,
      style: { color: '#ffffff', fontSize: '14px' }
    },
    xAxis: {
      type: 'datetime',
      labels: { 
        style: { color: '#8c8c8c', fontSize: '10px' } 
      },
      gridLineColor: '#404040',
      lineColor: '#404040',
      tickColor: '#404040'
    },
    yAxis: [{
      title: { 
        text: 'MTM Value (₹)', 
        style: { color: '#8c8c8c', fontSize: '11px' } 
      },
      labels: { 
        style: { color: '#8c8c8c', fontSize: '10px' },
        formatter: function() {
          return '₹' + Highcharts.numberFormat(this.value as number, 0);
        }
      },
      gridLineColor: '#404040',
      height: '70%'
    }, {
      title: { 
        text: 'P&L (₹)', 
        style: { color: '#8c8c8c', fontSize: '11px' } 
      },
      labels: { 
        style: { color: '#8c8c8c', fontSize: '10px' },
        formatter: function() {
          return '₹' + Highcharts.numberFormat(this.value as number, 0);
        }
      },
      gridLineColor: '#404040',
      top: '75%',
      height: '25%'
    }],
    series: [{
      name: 'MTM Value',
      type: 'line',
      data: chartData,
      yAxis: 0,
      color: '#1890ff',
      lineWidth: 2,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: true,
            radius: 4
          }
        }
      }
    }, {
      name: 'P&L',
      type: 'area',
      data: pnlData,
      yAxis: 1,
      color: 'rgba(82, 196, 26, 0.3)',
      lineColor: '#52c41a',
      lineWidth: 1,
      fillOpacity: 0.3,
      marker: {
        enabled: false
      }
    }],
    legend: {
      enabled: true,
      itemStyle: { color: '#ffffff', fontSize: '11px' },
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom'
    },
    tooltip: {
      backgroundColor: '#262626',
      borderColor: '#404040',
      style: {
        color: '#ffffff',
        fontSize: '11px'
      },
      formatter: function() {
        const point = this.point;
        const time = new Date(point.x as number);
        const value = Highcharts.numberFormat(point.y as number, 2);
        
        return `<b>${this.series.name}</b><br/>
                ${time.toLocaleString()}<br/>
                ₹${value}`;
      }
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      line: {
        animation: false
      },
      area: {
        animation: false
      }
    }
  };

  return (
    <div className="m2m-chart-container">
      {error && (
        <div style={{ 
          color: '#faad14', 
          fontSize: '12px', 
          marginBottom: '5px',
          textAlign: 'center'
        }}>
          ⚠️ Using mock data - {error}
        </div>
      )}
      <HighchartsReact
        highcharts={Highcharts}
        constructorType={'stockChart'}
        options={chartOptions}
      />
    </div>
  );
};

export default M2MChart;